import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
  type ExpandedState,
  type Header,
  type RowData,
  type SortingState,
  type RowSelectionState,
  type VisibilityState,
} from '@tanstack/react-table'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Collision,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  type SortingStrategy,
} from '@dnd-kit/sortable'
import { ArrowUpDown, ArrowUp, ArrowDown, GripVertical, Pencil } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { InlineEditCell, type InlineEditMove } from '@/components/InlineEditCell'
import { CREATIVE_LINE_ITEM_ENDED, type ValidationWarning } from '@/services/core/validationWarnings'
import type { InlineEdits, InlineEditSpec, InlineEditValue } from '@/lib/inlineEdit'
import type { AllPages, ListSort } from '@/services/core/paging'
import { Skeleton } from '@/components/ui/skeleton'
import { ColumnPicker, columnLabel } from '@/components/ColumnPicker'
import { useColumnsSlot, useListFetchAll, useTableActionsSlot } from '@/components/ListCard'
import { TableDownloadMenu } from '@/components/TableDownloadMenu'
import { CompactTableContext } from '@/lib/tableDensity'
import { RowWarningsContext } from '@/lib/rowWarnings'
import { useCompactTables } from '@/hooks/useCompactTables'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Minimum width in px for the header cell. */
    minWidth?: number
    /** Hidden by default and offered as an extra column in the column picker. */
    optional?: boolean
    /** Name shown in the column picker when the header is not plain text. */
    label?: string
    /**
     * Groups the column under its own heading in the column picker, instead of
     * with the standard or additional columns. The value is the heading's text.
     */
    section?: string
    /** Which edge the column's contents sit against. Numbers read right. */
    align?: 'left' | 'right'
    /**
     * Makes the column editable from the list. See `InlineEditSpec` — the
     * table opens the editor, stages the change and moves the keyboard on; the
     * spec only says how to read the field and what payload sets it.
     */
    edit?: InlineEditSpec<TData>
    /**
     * The column's value in a downloaded totals row — the raw figure behind
     * its footer. Columns that set it offer an Excel download with totals.
     */
    exportTotal?: unknown
    /**
     * The column's value in a download. Without it a download takes the
     * column's field, or — for a display-only column — the text its cell shows.
     */
    exportValue?: (row: TData) => unknown
    /** Leaves the column out of downloads, e.g. a column of action buttons. */
    export?: false
    /** Never used — keeps the augmented generics referenced. */
    __types?: [TData, TValue]
  }
}

/**
 * The row-selection column shrinks to the checkbox (`w-px`) and takes its gap
 * to the next column from padding, so the spacing is the same in every table.
 * A set width would only be a hint: auto-layout tables spread spare width over
 * their columns, which made the gap differ from table to table. It is 1px, not
 * 0: browsers read a zero width as no width, and when every other column has
 * one, all the spare width lands on the checkboxes. The right padding
 * overrides the table's own `[&:has([role=checkbox])]:pr-0` rule.
 */
const SELECT_CELL_CLASS = 'w-px pl-5 [&:has([role=checkbox])]:pr-3'

/**
 * The select column's width once a table is fixed, where `w-0` no longer
 * applies: its left padding (pl-5, or the card's spacing on an inset table),
 * the checkbox and pr-3. Every list's checkboxes then sit the same distance
 * from the first column, however its other columns were dragged.
 */
const SELECT_FIXED_WIDTH = 'calc(1.25rem + 1.75rem)'
const SELECT_FIXED_WIDTH_INSET = 'calc(var(--card-spacing, 1.25rem) + 1.75rem)'
/** The same width in px, for TanStack's sum of the column widths. */
const SELECT_COLUMN_SIZE = 52

/** Aligns a table's outer columns with the padding of its surrounding card. */
export const INSET_CLASS = [
  '[&_th:first-child]:pl-(--card-spacing)',
  '[&_td:first-child]:pl-(--card-spacing)',
  '[&_th:last-child]:pr-(--card-spacing)',
  '[&_td:last-child]:pr-(--card-spacing)',
].join(' ')

const COLUMNS_STORAGE_PREFIX = 'adnuntius.rui.columns.'
const ORDER_STORAGE_PREFIX = 'adnuntius.rui.columnOrder.'
const SIZING_STORAGE_PREFIX = 'adnuntius.rui.columnSizing.'
const COMPACT_STORAGE_PREFIX = 'adnuntius.rui.compactTable.'

/** Narrowest a column can be dragged to — still room for a short value. */
const MIN_COLUMN_WIDTH = 48

/** Which side of the hovered header the column will be inserted on. */
type DropSide = 'before' | 'after'

/**
 * Picks the header the cursor is horizontally inside — falling back to the
 * nearest one when the cursor is past the ends of the row — and records which
 * half of it the cursor is in. Distance-to-centre detection makes wide columns
 * nearly undraggable (their centre has to travel past a neighbour's centre)
 * and flickers when neighbouring widths differ.
 *
 * The side travels along in the collision data so that the insertion line and
 * the reorder on drop are the same decision, taken once, from the cursor.
 */
const columnCollision: CollisionDetection = ({
  droppableContainers,
  pointerCoordinates,
  collisionRect,
}) => {
  const x = pointerCoordinates?.x ?? collisionRect.left + collisionRect.width / 2
  let nearest: { id: string | number; distance: number; side: DropSide } | null = null
  for (const container of droppableContainers) {
    const rect = container.rect.current
    if (!rect) continue
    const side: DropSide = x < rect.left + rect.width / 2 ? 'before' : 'after'
    if (x >= rect.left && x <= rect.left + rect.width) {
      return [{ id: container.id, data: { droppableContainer: container, value: 0, side } }]
    }
    const distance = x < rect.left ? rect.left - x : x - (rect.left + rect.width)
    if (!nearest || distance < nearest.distance) {
      nearest = { id: container.id, distance, side }
    }
  }
  return nearest ? [{ id: nearest.id, data: { value: nearest.distance, side: nearest.side } }] : []
}

/** Reads the side back off the winning collision. */
function sideOf(collisions: Collision[] | null | undefined): DropSide {
  return (collisions?.[0]?.data as { side?: DropSide } | undefined)?.side ?? 'after'
}

/**
 * Headers stay put while dragging. Live-shifting variable-width columns
 * reflows the row under the cursor, which is what makes the drag jitter; an
 * insertion line shows where the column will land instead.
 */
const noShift: SortingStrategy = () => null

/**
 * Warnings that are expected once a campaign has run its course, so a list
 * doesn't flag them — the object's own page still shows them.
 */
const LIST_IGNORED_WARNINGS = new Set([CREATIVE_LINE_ITEM_ENDED])

/**
 * The server's validation warnings on a row, read off the `validationWarnings`
 * that every API object carries once its service maps it.
 */
function rowWarnings(row: unknown): ValidationWarning[] {
  const warnings = (row as { validationWarnings?: unknown } | null)?.validationWarnings
  if (!Array.isArray(warnings)) return []
  return (warnings as ValidationWarning[]).filter((w) => !LIST_IGNORED_WARNINGS.has(w.code))
}

/**
 * Sorting done by the server for a paged list. Sorting in the browser would
 * only reorder the page on screen, not the whole list.
 */
export interface ServerSort {
  sort: ListSort | null
  onChange: (sort: ListSort | null) => void
  /** The API field each sortable column orders by, by column id. Columns left out can't sort. */
  fields: Record<string, string>
}

function toSortingState(serverSort: ServerSort): SortingState {
  const { sort, fields } = serverSort
  const id = sort && Object.keys(fields).find((key) => fields[key] === sort.orderBy)
  return id ? [{ id, desc: sort.orderByDirection === 'DESCENDING' }] : []
}

function toListSort(sorting: SortingState, fields: Record<string, string>): ListSort | null {
  const first = sorting[0]
  const orderBy = first && fields[first.id]
  return orderBy ? { orderBy, orderByDirection: first.desc ? 'DESCENDING' : 'ASCENDING' } : null
}

/** Mirrors TanStack's own id derivation so we can read ids off raw column defs. */
function columnIdOf<TData>(column: ColumnDef<TData, unknown>): string | undefined {
  if (column.id) return column.id
  const key = (column as { accessorKey?: string | number }).accessorKey
  return typeof key === 'string' ? key.replace(/\./g, '_') : key?.toString()
}

function defaultVisibility<TData>(columns: ColumnDef<TData, unknown>[]): VisibilityState {
  const visibility: VisibilityState = {}
  columns.forEach((column) => {
    const id = columnIdOf(column)
    if (id && column.meta?.optional) visibility[id] = false
  })
  return visibility
}

function readStoredVisibility(key: string): VisibilityState {
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_PREFIX + key)
    return raw ? (JSON.parse(raw) as VisibilityState) : {}
  } catch {
    return {}
  }
}

function defaultOrder<TData>(columns: ColumnDef<TData, unknown>[]): ColumnOrderState {
  return columns.map(columnIdOf).filter((id): id is string => !!id)
}

function readStoredSizing(key: string): ColumnSizingState {
  try {
    const raw = localStorage.getItem(SIZING_STORAGE_PREFIX + key)
    return raw ? (JSON.parse(raw) as ColumnSizingState) : {}
  } catch {
    return {}
  }
}

function readStoredOrder(key: string): ColumnOrderState {
  try {
    const raw = localStorage.getItem(ORDER_STORAGE_PREFIX + key)
    return raw ? (JSON.parse(raw) as ColumnOrderState) : []
  } catch {
    return []
  }
}

/**
 * Applies a remembered order to the current column defs: ids that no longer
 * exist are dropped, and columns added since the order was stored fall in at
 * the position their def gives them.
 */
function mergeOrder(defaults: ColumnOrderState, stored: ColumnOrderState): ColumnOrderState {
  const known = new Set(defaults)
  const order = stored.filter((id) => known.has(id))
  defaults.forEach((id, index) => {
    if (!order.includes(id)) order.splice(Math.min(index, order.length), 0, id)
  })
  return order
}

/**
 * How a table lays out its columns. It starts `auto`: the browser sizes each
 * column to its content and a set size is only a hint. The first time a column
 * is dragged wider or narrower it turns `fixed`, every column taking the width
 * it was measured at, so a column can then be made narrower than its content.
 */
type ColumnLayout = 'auto' | 'fixed'

interface HeadSizing {
  layout: ColumnLayout
  /** The last column takes up whatever room is left, so the table fills its card. */
  isLast: boolean
  /** Lined up with its card, so the first column is padded by the card's spacing. */
  inset: boolean
  /** Starts a drag on the column's right edge. */
  onResizeStart: (e: React.MouseEvent | React.TouchEvent) => void
}

function headStyle<TData>(header: Header<TData, unknown>, sizing: HeadSizing): React.CSSProperties {
  if (sizing.layout === 'fixed') {
    if (header.column.id === 'select') {
      return { width: sizing.inset ? SELECT_FIXED_WIDTH_INSET : SELECT_FIXED_WIDTH }
    }
    return { width: sizing.isLast ? undefined : header.getSize() }
  }
  return {
    width:
      header.column.id !== 'select' && header.getSize() !== 150 ? header.getSize() : undefined,
    minWidth: header.column.columnDef.meta?.minWidth,
  }
}

function headClass<TData>(header: Header<TData, unknown>): string {
  return cn(
    'relative',
    header.column.getCanSort() && 'cursor-pointer select-none',
    header.column.id === 'select' && SELECT_CELL_CLASS,
    // The label sits in a flex row, so the row itself has to move over.
    header.column.columnDef.meta?.align === 'right' && '[&>div]:justify-end text-right',
  )
}

/**
 * A mouse drag ends in a click on whatever the press and the release have in
 * common — usually the header — which would sort by the column just resized.
 * The click comes straight after the release, so the swallow is dropped on the
 * next tick in case none does (touch).
 */
function swallowClickAfterDrag() {
  const swallow = (e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const release = () => {
    window.removeEventListener('mouseup', release)
    window.removeEventListener('touchend', release)
    setTimeout(() => window.removeEventListener('click', swallow, true), 0)
  }
  window.addEventListener('click', swallow, true)
  window.addEventListener('mouseup', release)
  window.addEventListener('touchend', release)
}

/**
 * The grab area on a header's right edge. It sits over the column border, so
 * it is found where people look for it, and shows a line while hovered or held.
 */
function ResizeHandle<TData>({
  header,
  onResizeStart,
}: {
  header: Header<TData, unknown>
  onResizeStart: HeadSizing['onResizeStart']
}) {
  const { t } = useTranslation()
  if (!header.column.getCanResize()) return null
  const resizing = header.column.getIsResizing()
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('common.resizeColumn', { column: columnLabel(header.column) })}
      onMouseDown={onResizeStart}
      onTouchStart={onResizeStart}
      onClick={(e) => e.stopPropagation()}
      className="group/resize absolute inset-y-0 -right-1 z-10 flex w-2 cursor-col-resize touch-none justify-center select-none"
    >
      <span
        className={cn(
          'h-full w-0.5 bg-(--ad-primary) opacity-0 transition-opacity group-hover/resize:opacity-100',
          resizing && 'opacity-100',
        )}
      />
    </div>
  )
}

/** Header text plus the sort direction arrow. */
function HeadLabel<TData>({ header }: { header: Header<TData, unknown> }) {
  const { column } = header
  const sorted = column.getIsSorted()
  return (
    <>
      {header.isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}
      {column.getCanSort() && (
        <span className="text-(--ad-text-faint)">
          {sorted === 'asc' ? (
            <ArrowUp className="size-3" />
          ) : sorted === 'desc' ? (
            <ArrowDown className="size-3" />
          ) : (
            <ArrowUpDown className="size-3" />
          )}
        </span>
      )}
    </>
  )
}

function StaticHead<TData>({
  header,
  sizing,
  className,
}: {
  header: Header<TData, unknown>
  sizing: HeadSizing
  className?: string
}) {
  return (
    <TableHead
      style={headStyle(header, sizing)}
      className={cn(headClass(header), className)}
      onClick={header.column.getToggleSortingHandler()}
    >
      <div className="flex min-w-0 items-center gap-1 overflow-hidden">
        <HeadLabel header={header} />
      </div>
      <ResizeHandle header={header} onResizeStart={sizing.onResizeStart} />
    </TableHead>
  )
}

/**
 * Header cell that can be dragged sideways to move its column. The grip is a
 * separate handle so that clicking anywhere else still toggles sorting.
 */
function DraggableHead<TData>({
  header,
  sizing,
  dropSide,
  className,
}: {
  header: Header<TData, unknown>
  sizing: HeadSizing
  /** Edge to draw the insertion line on while a column hovers this one. */
  dropSide?: DropSide
  className?: string
}) {
  const { t } = useTranslation()
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, isDragging } = useSortable({
    id: header.column.id,
  })

  return (
    <TableHead
      ref={setNodeRef}
      style={headStyle(header, sizing)}
      className={cn(
        headClass(header),
        'group',
        isDragging && 'bg-(--ad-primary-soft) opacity-60',
        className,
      )}
      onClick={header.column.getToggleSortingHandler()}
    >
      {dropSide && (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute inset-y-0 w-0.5 bg-(--ad-primary)',
            dropSide === 'before' ? 'left-0' : '-right-px',
          )}
        />
      )}
      <div className="flex min-w-0 items-center gap-1 overflow-hidden">
        <HeadLabel header={header} />
        <button
          type="button"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          aria-label={t('common.reorderColumn', { column: columnLabel(header.column) })}
          className="-m-1 cursor-grab touch-none p-1 text-(--ad-text-faint) opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 active:cursor-grabbing"
        >
          <GripVertical className="size-3" />
        </button>
      </div>
      <ResizeHandle header={header} onResizeStart={sizing.onResizeStart} />
    </TableHead>
  )
}

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[]
  data: TData[]
  loading?: boolean
  emptyMessage?: string
  onRowClick?: (row: TData) => void
  /**
   * The row's own page. A click goes there unless onRowClick handles it, and a
   * Cmd/Ctrl- or middle-click opens it in a new tab. Leave it undefined for a
   * row that cannot be opened on its own.
   */
  rowHref?: (row: TData) => string | undefined
  /** Pass to enable row selection. */
  selection?: TData[]
  onSelectionChange?: (rows: TData[]) => void
  /** Field to use as unique row id. Defaults to "id" */
  getRowId?: (row: TData) => string
  /** Number of skeleton rows to show while loading */
  skeletonRows?: number
  /**
   * Enables the column picker above the table. Columns flagged with
   * `meta.optional` start hidden; the choice is remembered under this key.
   */
  columnsKey?: string
  /**
   * Offers the list as a CSV or Excel download beside the column picker.
   * On for every list with a `columnsKey`; turn it off where the page has its
   * own download.
   */
  downloadable?: boolean
  /**
   * Remembers column widths dragged by hand under this key, for a list without
   * a column picker. Lists with a `columnsKey` remember them under that.
   */
  sizingKey?: string
  /**
   * Caps the table's height in px, scrolling the rows past a header that stays
   * put. Leave unset to let the table run to its full length.
   */
  maxHeight?: number
  /**
   * Lines the outer columns up with the padding of the card around the table,
   * instead of letting them sit right against its edge.
   */
  inset?: boolean
  /**
   * Staged inline edits, from `useInlineEdits`. Passing this together with
   * `onEditChange` puts an edit toggle above the list, which turns every
   * column carrying `meta.edit` into an editable cell.
   */
  edits?: InlineEdits<TData>
  onEditChange?: (
    row: TData,
    columnId: string,
    value: InlineEditValue,
    spec: InlineEditSpec<TData>,
  ) => void
  /**
   * Nests rows under a parent row, e.g. the sites inside each month. Parents
   * start expanded and sort with their children kept beneath them; a column's
   * cell can toggle its row with `row.getToggleExpandedHandler()`.
   */
  getSubRows?: (row: TData) => TData[] | undefined
  /**
   * Which parent rows start expanded, by row id — `{}` starts them all
   * collapsed. Every parent starts expanded when left out.
   */
  defaultExpanded?: ExpandedState
  /** Sorts on the server instead of in the browser — for lists the server pages. */
  serverSort?: ServerSort
}

export function DataTable<TData>({
  columns,
  data,
  loading,
  emptyMessage = 'No results.',
  onRowClick,
  rowHref,
  selection,
  onSelectionChange,
  getRowId,
  skeletonRows = 5,
  columnsKey,
  downloadable = true,
  sizingKey,
  maxHeight,
  inset,
  edits,
  onEditChange,
  getSubRows,
  defaultExpanded = true,
  serverSort,
}: DataTableProps<TData>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const columnsSlot = useColumnsSlot()
  const fetchAll = useListFetchAll() as (() => Promise<AllPages<TData>>) | undefined
  const actionsSlot = useTableActionsSlot()
  const [localSorting, setLocalSorting] = useState<SortingState>([])
  const sorting = serverSort ? toSortingState(serverSort) : localSorting
  const serverSortFields = serverSort?.fields

  // A server-sorted list can only sort by the columns the API can order by.
  const tableColumns = useMemo(
    () =>
      serverSortFields
        ? columns.map((column) => {
            const id = columnIdOf(column)
            return id && serverSortFields[id] ? column : { ...column, enableSorting: false }
          })
        : columns,
    [columns, serverSortFields]
  )
  const [expanded, setExpanded] = useState<ExpandedState>(defaultExpanded)
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => ({
    ...defaultVisibility(columns),
    ...(columnsKey ? readStoredVisibility(columnsKey) : {}),
  }))
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() => {
    const defaults = defaultOrder(columns)
    return columnsKey ? mergeOrder(defaults, readStoredOrder(columnsKey)) : defaults
  })

  // Sizes are only kept once someone has dragged a column; until then the
  // table lays itself out from its content.
  const sizingStoreKey = columnsKey ?? sizingKey
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    sizingStoreKey ? readStoredSizing(sizingStoreKey) : {},
  )
  const layout: ColumnLayout = Object.keys(columnSizing).length > 0 ? 'fixed' : 'auto'

  useEffect(() => {
    if (!sizingStoreKey) return
    try {
      if (Object.keys(columnSizing).length) {
        localStorage.setItem(SIZING_STORAGE_PREFIX + sizingStoreKey, JSON.stringify(columnSizing))
      } else {
        localStorage.removeItem(SIZING_STORAGE_PREFIX + sizingStoreKey)
      }
    } catch {
      // storage unavailable — resizing still works for this session
    }
  }, [sizingStoreKey, columnSizing])

  useEffect(() => {
    if (!columnsKey) return
    try {
      localStorage.setItem(COLUMNS_STORAGE_PREFIX + columnsKey, JSON.stringify(columnVisibility))
    } catch {
      // storage unavailable — the picker still works for this session
    }
  }, [columnsKey, columnVisibility])

  useEffect(() => {
    if (!columnsKey) return
    try {
      localStorage.setItem(ORDER_STORAGE_PREFIX + columnsKey, JSON.stringify(columnOrder))
    } catch {
      // storage unavailable — dragging still works for this session
    }
  }, [columnsKey, columnOrder])

  // Compact view: no id under the name and tighter rows. Lists follow the
  // user's preference unless one has been switched on its own, which is
  // remembered per list.
  const { compact: compactByDefault } = useCompactTables()
  const [compactOverride, setCompactOverride] = useState<boolean | null>(() => {
    if (!columnsKey) return null
    try {
      const stored = localStorage.getItem(COMPACT_STORAGE_PREFIX + columnsKey)
      return stored === null ? null : stored === 'true'
    } catch {
      return null
    }
  })
  const compact = compactOverride ?? compactByDefault
  const changeCompact = useCallback(
    (next: boolean) => {
      // Back in line with the preference, the list just follows it again.
      const override = next === compactByDefault ? null : next
      setCompactOverride(override)
      if (!columnsKey) return
      try {
        if (override === null) localStorage.removeItem(COMPACT_STORAGE_PREFIX + columnsKey)
        else localStorage.setItem(COMPACT_STORAGE_PREFIX + columnsKey, String(override))
      } catch {
        // storage unavailable — the view still changes for this session
      }
    },
    [columnsKey, compactByDefault],
  )

  const resetColumns = useCallback(() => {
    setColumnVisibility(defaultVisibility(columns))
    setColumnOrder(defaultOrder(columns))
    setColumnSizing({})
  }, [columns])

  // Build row selection state from the selection prop
  const selectable = !!onSelectionChange
  const rowSelection: RowSelectionState = {}
  if (selectable && selection) {
    const idFn = getRowId ?? ((row: TData) => (row as Record<string, unknown>).id as string)
    selection.forEach((item) => {
      rowSelection[idFn(item)] = true
    })
  }

  const table = useReactTable({
    data,
    columns: tableColumns,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      columnSizing,
      ...(getSubRows ? { expanded } : {}),
      ...(selectable ? { rowSelection } : {}),
    },
    onSortingChange: serverSort
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(sorting) : updater
          serverSort.onChange(toListSort(next, serverSort.fields))
        }
      : setLocalSorting,
    manualSorting: !!serverSort,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: { minSize: MIN_COLUMN_WIDTH },
    ...(selectable
      ? {
          enableRowSelection: true,
          onRowSelectionChange: (updater) => {
            const next =
              typeof updater === 'function' ? updater(rowSelection) : updater
            const selectedRows = data.filter((row) => {
              const idFn =
                getRowId ?? ((r: TData) => (r as Record<string, unknown>).id as string)
              return next[idFn(row)]
            })
            onSelectionChange!(selectedRows)
          },
        }
      : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getSubRows
      ? { getSubRows, onExpandedChange: setExpanded, getExpandedRowModel: getExpandedRowModel() }
      : {}),
    getRowId: getRowId ?? ((row) => (row as Record<string, unknown>).id as string),
  })

  const visibleColumns = table.getVisibleFlatColumns()

  // ─── Resizing ──────────────────────────────────────────────────────────
  const tableRef = useRef<HTMLTableElement>(null)

  // The first drag freezes the layout the browser chose — each column kept at
  // the width it has on screen — so nothing jumps as the table turns fixed.
  // It has to land before TanStack reads the column's starting width, hence
  // the synchronous render.
  const resizeStart = (header: Header<TData, unknown>) => (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation()
    swallowClickAfterDrag()
    const heads = tableRef.current?.querySelectorAll<HTMLTableCellElement>('thead tr:first-child > th')
    if (layout === 'auto' && heads) {
      const measured: ColumnSizingState = {}
      visibleColumns.forEach((column, i) => {
        const width = heads[i]?.getBoundingClientRect().width
        if (width) measured[column.id] = Math.round(width)
      })
      flushSync(() => setColumnSizing(measured))
    }
    header.getResizeHandler()(e)
  }

  const sizingOf = (header: Header<TData, unknown>): HeadSizing => ({
    layout,
    isLast: header.column.id === visibleColumns[visibleColumns.length - 1]?.id,
    inset: !!inset,
    onResizeStart: resizeStart(header),
  })

  // A fixed table is as wide as its columns, and never narrower than its card:
  // the last column, left without a width, takes up the difference.
  const tableStyle: React.CSSProperties | undefined =
    layout === 'fixed' ? { tableLayout: 'fixed', width: `max(${table.getTotalSize()}px, 100%)` } : undefined

  // Columns are draggable wherever the picker is — both need a key to remember
  // the choice. The selection checkbox is pinned first and stays out of it.
  const reorderable = !!columnsKey
  const draggableIds = useMemo(
    () => visibleColumns.filter((c) => c.getCanHide()).map((c) => c.id),
    [visibleColumns],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // The column being dragged and the one it is hovering, so the header row can
  // draw an insertion line instead of shuffling itself around under the cursor.
  const [activeId, setActiveId] = useState<string | null>(null)
  const [drop, setDrop] = useState<{ overId: string; side: DropSide } | null>(null)

  const handleDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveId(active.id as string)
    setDrop(null)
  }, [])

  // dnd-kit only fires this when the hovered column changes, so track the move
  // as well — the cursor crossing a column's midpoint flips the side without
  // changing which column is hovered.
  const handleDragMove = useCallback(({ over, collisions }: DragMoveEvent) => {
    setDrop(over ? { overId: over.id as string, side: sideOf(collisions) } : null)
  }, [])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setDrop(null)
  }, [])

  const handleDragEnd = useCallback(
    ({ active, over, collisions }: DragEndEvent) => {
      setActiveId(null)
      setDrop(null)
      if (!over) return
      const side = sideOf(collisions)
      setColumnOrder((current) => {
        const ids = current.length ? current : defaultOrder(columns)
        const moved = active.id as string
        if (!ids.includes(moved)) return ids
        // Insert into the list with the column pulled out, so the index means
        // the same thing here as the insertion line means on screen.
        const rest = ids.filter((id) => id !== moved)
        const overIndex = rest.indexOf(over.id as string)
        if (overIndex < 0) return ids
        // Never drop a column ahead of a pinned one, such as the checkbox.
        const firstMovable = rest.findIndex((id) => draggableIds.includes(id))
        const at = Math.max(overIndex + (side === 'after' ? 1 : 0), Math.max(firstMovable, 0))
        rest.splice(at, 0, moved)
        return rest
      })
    },
    [columns, draggableIds],
  )

  // The insertion line goes on the edge of the hovered header nearest the
  // cursor, and is left off when that gap is where the column already sits.
  const dropSideOf = (id: string): DropSide | undefined => {
    if (!activeId || !drop || drop.overId !== id || drop.overId === activeId) return undefined
    const visibleIds = visibleColumns.map((c) => c.id)
    const overIndex = visibleIds.indexOf(drop.overId)
    const neighbour = visibleIds[drop.side === 'before' ? overIndex - 1 : overIndex + 1]
    return neighbour === activeId ? undefined : drop.side
  }

  const activeColumn = activeId ? visibleColumns.find((c) => c.id === activeId) : undefined

  // A footer row only when some column has something to say in it, e.g. totals.
  const hasFooter = visibleColumns.some((c) => c.columnDef.footer !== undefined)

  // ─── Inline editing ────────────────────────────────────────────────────
  // Editing is a mode rather than the default: in it, rows stop navigating and
  // the columns that carry an edit spec become editable in place. Outside it
  // the list behaves exactly as a read-only list does.
  const editable = !!onEditChange
  const [editingMode, setEditingMode] = useState(false)
  const [activeCell, setActiveCell] = useState<{ rowId: string; columnId: string } | null>(null)
  const editsPending = !!edits && Object.keys(edits).length > 0

  const editableIds = useMemo(
    () => visibleColumns.filter((c) => !!c.columnDef.meta?.edit).map((c) => c.id),
    [visibleColumns],
  )

  // Escape steps back out: the open editor closes it first, so this only sees
  // the key once no cell is being edited.
  useEffect(() => {
    if (!editingMode || activeCell || editsPending) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditingMode(false)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [editingMode, activeCell, editsPending])

  const rows = table.getRowModel().rows

  /**
   * The cell the keyboard lands on next, skipping the ones a row does not let
   * be edited. Tab runs along the row and wraps into the next one; Enter and
   * the arrow keys stay in the column.
   */
  const nextCell = (
    from: { rowId: string; columnId: string },
    move: InlineEditMove,
  ): { rowId: string; columnId: string } | null => {
    let rowIndex = rows.findIndex((r) => r.id === from.rowId)
    let colIndex = editableIds.indexOf(from.columnId)
    if (rowIndex < 0 || colIndex < 0) return null
    for (let step = 0; step < rows.length * editableIds.length + 1; step++) {
      if (move === 'next' || move === 'prev') {
        colIndex += move === 'next' ? 1 : -1
        if (colIndex >= editableIds.length) {
          colIndex = 0
          rowIndex += 1
        } else if (colIndex < 0) {
          colIndex = editableIds.length - 1
          rowIndex -= 1
        }
      } else {
        rowIndex += move === 'down' ? 1 : -1
      }
      if (rowIndex < 0 || rowIndex >= rows.length) return null
      const target = rows[rowIndex]
      const columnId = editableIds[colIndex]
      const spec = target.getVisibleCells().find((c) => c.column.id === columnId)?.column.columnDef
        .meta?.edit
      if (spec && !(spec.readOnly?.(target.original) ?? false)) {
        return { rowId: target.id, columnId }
      }
    }
    return null
  }

  const commitEdit =
    (row: TData, rowId: string, columnId: string, spec: InlineEditSpec<TData>) =>
    (value: InlineEditValue, move?: InlineEditMove) => {
      onEditChange?.(row, columnId, value, spec)
      // 'stay' leaves the editor open — it has more to be set in it.
      if (move === 'stay') return
      setActiveCell(move ? nextCell({ rowId, columnId }, move) : null)
    }

  const editToggle =
    editable && editableIds.length > 0 ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-pressed={editingMode}
        // Leaving the mode would hide changes that have not been saved yet.
        disabled={editingMode && editsPending}
        // On, the button carries the colour the editable cells are picked out
        // in, so the mode the list is in is never in doubt.
        className={cn(
          editingMode &&
            'border-(--ad-primary) bg-(--ad-primary-soft) text-(--ad-primary) ring-2 ring-(--ad-primary) hover:bg-(--ad-primary-soft) hover:text-(--ad-primary) disabled:opacity-100',
        )}
        onClick={() => {
          setEditingMode((on) => !on)
          setActiveCell(null)
        }}
      >
        <Pencil className="size-4" />
        {t('inlineEdit.toggle')}
      </Button>
    ) : null

  // Not memoised: the TanStack table instance is a stable reference, so a
  // cached element would keep showing stale checkmarks after a toggle.
  const picker = columnsKey ? (
    <>
      <ColumnPicker table={table} onReset={resetColumns} compact={compact} onCompactChange={changeCompact} />
      {downloadable && (
        <TableDownloadMenu
          table={table}
          filename={columnsKey}
          getTableElement={() => tableRef.current}
          fetchAll={fetchAll}
        />
      )}
    </>
  ) : null

  // Inside a ListCard the table's controls belong in the card header: the
  // column picker next to the list name, and the edit toggle at the end of the
  // row, after the page's own actions. On its own the table carries a strip
  // for them above the header row.
  const toolbar = (
    <>
      {columnsSlot
        ? picker && columnsSlot.element && createPortal(picker, columnsSlot.element)
        : null}
      {actionsSlot
        ? editToggle && actionsSlot.element && createPortal(editToggle, actionsSlot.element)
        : null}
      {!columnsSlot && !actionsSlot && (picker || editToggle) && (
        <div className="flex items-center justify-end gap-2 border-b border-(--ad-border) px-4 py-2">
          {picker}
          {editToggle}
        </div>
      )}
    </>
  )

  // A capped table scrolls its rows, so the header has to stick to the top of
  // that scroll container and paint over the rows passing under it.
  const scrollProps = maxHeight
    ? { containerClassName: 'overflow-y-auto', containerStyle: { maxHeight } }
    : {}
  const stickyHead = maxHeight ? 'sticky top-0 z-10' : undefined
  // Padding goes on the edge cells rather than a wrapper, so row borders and
  // hover still run the full width of the card.
  const insetClass = cn(inset && INSET_CLASS, compact && '[&_td]:py-1')

  if (loading) {
    return (
      <>
        {toolbar}
        <Table className={insetClass} {...scrollProps}>
          <TableHeader>
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead key={col.id} className={stickyHead}>
                  <Skeleton className="h-4 w-20" />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: skeletonRows }).map((_, i) => (
              <TableRow key={i}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.id}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    )
  }

  return (
    <CompactTableContext.Provider value={compact}>
      <DndContext
        sensors={sensors}
        collisionDetection={columnCollision}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
      >
        {toolbar}
        <Table ref={tableRef} className={insetClass} style={tableStyle} {...scrollProps}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                <SortableContext items={draggableIds} strategy={noShift}>
                  {headerGroup.headers.map((header) =>
                    reorderable && draggableIds.includes(header.column.id) ? (
                      <DraggableHead
                        key={header.id}
                        header={header}
                        sizing={sizingOf(header)}
                        dropSide={dropSideOf(header.column.id)}
                        className={stickyHead}
                      />
                    ) : (
                      <StaticHead
                        key={header.id}
                        header={header}
                        sizing={sizingOf(header)}
                        className={stickyHead}
                      />
                    ),
                  )}
                </SortableContext>
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                // Rows the server has warnings about carry a bar down their
                // first cell — red when they stop the object serving — and
                // their name cell lists the warnings behind an icon. They take
                // on a tint on hover, except in edit mode, where the tint
                // marks staged cells.
                const warnings = rowWarnings(row.original)
                const blocking = warnings.some((w) => w.preventsAdServing)
                return (
                  <RowWarningsContext.Provider key={row.id} value={warnings}>
                    <TableRow
                      data-state={row.getIsSelected() && 'selected'}
                      data-row-id={row.id}
                      className={cn(
                        (onRowClick || rowHref) && !editingMode && 'cursor-pointer',
                        // A parent row reads as the heading of the rows under it.
                        row.subRows.length > 0 && 'bg-(--ad-neutral-soft) font-semibold',
                        warnings.length > 0 &&
                          (blocking
                            ? '[&>td:first-child]:shadow-[inset_3px_0_0_var(--ad-danger),inset_10px_0_10px_-8px_color-mix(in_oklab,var(--ad-danger)_45%,transparent)]'
                            : '[&>td:first-child]:shadow-[inset_3px_0_0_var(--ad-warning),inset_10px_0_10px_-8px_color-mix(in_oklab,var(--ad-warning)_45%,transparent)]'),
                        warnings.length > 0 &&
                          !editingMode &&
                          (blocking
                            ? 'hover:bg-(--ad-danger-soft)'
                            : 'hover:bg-(--ad-warning-soft)'),
                      )}
                      onClick={(e) => {
                        // A row being edited is a row being worked on, not one to
                        // open — and checkboxes never navigate either.
                        if (editingMode) return
                        if ((e.target as HTMLElement).closest('[role=checkbox], input[type=checkbox]')) return
                        const href = rowHref?.(row.original)
                        if (href && (e.metaKey || e.ctrlKey)) {
                          window.open(href, '_blank', 'noopener')
                        } else if (onRowClick) {
                          onRowClick(row.original)
                        } else if (href) {
                          navigate(href)
                        }
                      }}
                      onAuxClick={(e) => {
                        if (e.button !== 1 || editingMode) return
                        const href = rowHref?.(row.original)
                        if (href) window.open(href, '_blank', 'noopener')
                      }}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const spec = cell.column.columnDef.meta?.edit
                        const staged = edits?.[row.id]?.cells?.[cell.column.id]
                        const content = flexRender(cell.column.columnDef.cell, cell.getContext())
                        return (
                          <TableCell
                            key={cell.id}
                            data-column-id={cell.column.id}
                            className={cn(
                              cell.column.id === 'select' && SELECT_CELL_CLASS,
                              layout === 'fixed' && 'overflow-hidden text-ellipsis',
                              staged && 'bg-(--ad-warning-soft)',
                              cell.column.columnDef.meta?.align === 'right' && 'text-right',
                            )}
                          >
                            {editingMode && spec ? (
                              <InlineEditCell
                                row={row.original}
                                columnId={cell.column.id}
                                spec={spec}
                                staged={staged}
                                active={
                                  activeCell?.rowId === row.id &&
                                  activeCell.columnId === cell.column.id
                                }
                                onActivate={() =>
                                  setActiveCell({ rowId: row.id, columnId: cell.column.id })
                                }
                                onClose={() => setActiveCell(null)}
                                onCommit={commitEdit(row.original, row.id, cell.column.id, spec)}
                              >
                                {content}
                              </InlineEditCell>
                            ) : (
                              content
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  </RowWarningsContext.Provider>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={visibleColumns.length} className="h-24 text-center text-(--ad-text-muted)">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {hasFooter && (
            <TableFooter>
              {table.getFooterGroups().map((footerGroup) => (
                <TableRow key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      className={cn(
                        header.column.columnDef.meta?.align === 'right' && 'text-right'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.footer, header.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableFooter>
          )}
        </Table>
        <DragOverlay dropAnimation={null}>
          {activeColumn && (
            <div className="flex cursor-grabbing items-center gap-1 rounded-md border border-(--ad-border) bg-(--ad-surface) px-2 py-1 text-xs font-medium text-(--ad-text) shadow-md">
              <GripVertical className="size-3 text-(--ad-text-faint)" />
              {columnLabel(activeColumn)}
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </CompactTableContext.Provider>
  )
}

/** Marks a column as an extra field, hidden until picked in the column picker. */
export function optionalColumn<TData>(column: ColumnDef<TData, unknown>): ColumnDef<TData, unknown> {
  return { ...column, meta: { ...column.meta, optional: true } }
}

/** Helper: checkbox column for row selection */
export function selectionColumn<TData>(): ColumnDef<TData, unknown> {
  return {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      />
    ),
    // Pinned, so a width remembered from before it was narrow cannot stretch it.
    size: SELECT_COLUMN_SIZE,
    minSize: SELECT_COLUMN_SIZE,
    maxSize: SELECT_COLUMN_SIZE,
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
  }
}
